import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Self,
  ViewChild,
  ChangeDetectorRef,
} from "@angular/core";
import { ErrorStateMatcher } from "@angular/material/core";
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from "@angular/material/autocomplete";
import { ControlValueAccessor, FormControl, NgControl } from "@angular/forms";
import {
  Country,
  COUNTRIES_DB,
} from "@angular-material-extensions/select-country";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import {
  getCountryDisplayName as getLocalizedCountryDisplayName,
  getCountryFlagUrl as getLocalizedCountryFlagUrl,
} from "../../card-orders/card-order.util";

@Component({
  selector: "app-country-select",
  templateUrl: "./country-select.component.html",
  styleUrls: ["./country-select.component.scss"],
  standalone: false,
})
export class CountrySelectComponent
  implements ControlValueAccessor, OnInit, OnDestroy
{
  @Input() disabled = false;
  @Input() label = "";
  @Input() subscriptSizing: "fixed" | "dynamic" = "dynamic";
  @Input() errorStateMatcher?: ErrorStateMatcher;
  @Input() required = false;

  @ViewChild(MatAutocompleteTrigger)
  autocompleteTrigger?: MatAutocompleteTrigger;

  private readonly defaultErrorStateMatcher = new ErrorStateMatcher();

  readonly countriesList: Country[] = COUNTRIES_DB;
  filteredCountries: Country[] = [];
  readonly searchControl = new FormControl("");
  value = "";

  private readonly destroy$ = new Subject<void>();
  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private pendingValue: string | null = null;
  private initialized = false;

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    private readonly translate: TranslateService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.initialized = true;
    this.filteredCountries = [...this.countriesList];
    this.searchControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.filterCountries();
      });
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.value) {
        this.searchControl.setValue(this.getCountryName(this.value), {
          emitEvent: false,
        });
      }
      this.filterCountries();
      this.cdr.markForCheck();
    });
    if (this.pendingValue !== null) {
      this.applyValue(this.pendingValue);
      this.pendingValue = null;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: string | null): void {
    const normalized = (value ?? "").trim().toUpperCase();
    if (!this.initialized) {
      this.pendingValue = normalized;
      this.value = normalized;
      return;
    }
    this.applyValue(normalized);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.searchControl.disable({ emitEvent: false });
      return;
    }
    this.searchControl.enable({ emitEvent: false });
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const country = event.option.value as Country;
    this.selectCountry(country.alpha2Code);
  }

  onInputClick(): void {
    if (this.disabled) {
      return;
    }
    if (
      this.value &&
      this.searchControl.value === this.getCountryName(this.value)
    ) {
      this.searchControl.setValue("", { emitEvent: true });
    }
    this.autocompleteTrigger?.openPanel();
  }

  onChevronClick(): void {
    if (this.disabled) {
      return;
    }
    this.onInputClick();
  }

  onInputBlur(): void {
    this.onTouched();
    const query = this.searchControl.value?.trim() ?? "";
    if (!query) {
      this.searchControl.setValue(
        this.value ? this.getCountryName(this.value) : "",
        { emitEvent: false },
      );
      return;
    }
    const exactMatch = this.countriesList.find(
      (country) =>
        country.name.toLowerCase() === query.toLowerCase() ||
        country.alpha2Code.toLowerCase() === query.toLowerCase(),
    );
    if (exactMatch) {
      this.selectCountry(exactMatch.alpha2Code);
      return;
    }
    this.searchControl.setValue(
      this.value ? this.getCountryName(this.value) : "",
      { emitEvent: false },
    );
  }

  trackByCountryCode(_index: number, country: Country): string {
    return country.alpha2Code;
  }

  filterCountries(): void {
    const raw = this.searchControl.value?.toLowerCase().trim() ?? "";
    const query =
      !raw ||
      (this.value && raw === this.getCountryName(this.value).toLowerCase())
        ? ""
        : raw;
    this.filteredCountries = query
      ? this.countriesList.filter((country) => {
          const localizedName = this.getCountryName(country.alpha2Code);
          return (
            localizedName.toLowerCase().includes(query) ||
            country.name.toLowerCase().includes(query) ||
            country.alpha2Code.toLowerCase().includes(query)
          );
        })
      : [...this.countriesList];

    if (
      this.value &&
      !this.filteredCountries.some(
        (country) => country.alpha2Code === this.value,
      )
    ) {
      const selected = this.countriesList.find(
        (country) => country.alpha2Code === this.value,
      );
      if (selected) {
        this.filteredCountries = [selected, ...this.filteredCountries];
      }
    }
  }

  getCountryName(alpha2Code: string): string {
    return getLocalizedCountryDisplayName(
      alpha2Code,
      this.translate.currentLang || "en",
    );
  }

  getCountryFlagUrl(alpha2Code: string): string {
    return getLocalizedCountryFlagUrl(alpha2Code);
  }

  showInvalidState(): boolean {
    const control = this.ngControl?.control;
    if (!control?.invalid) {
      return false;
    }
    const matcher = this.errorStateMatcher ?? this.defaultErrorStateMatcher;
    return matcher.isErrorState(control as FormControl, null);
  }

  showRequiredMarker(): boolean {
    if (this.required) {
      return true;
    }
    const control = this.ngControl?.control;
    if (!control?.validator) {
      return false;
    }
    const result = control.validator(new FormControl());
    return !!result?.["required"];
  }

  private applyValue(normalized: string): void {
    this.value = normalized;
    this.searchControl.setValue(
      this.value ? this.getCountryName(this.value) : "",
      { emitEvent: false },
    );
    this.filterCountries();
    this.cdr.markForCheck();
  }

  private selectCountry(alpha2Code: string): void {
    this.value = alpha2Code.trim().toUpperCase();
    this.searchControl.setValue(
      this.value ? this.getCountryName(this.value) : "",
      { emitEvent: false },
    );
    this.onChange(this.value);
    this.onTouched();
    this.cdr.markForCheck();
  }
}
